import { useMemo } from 'react'
import { getGameCourse, getGameTee, getMixedTees } from '../constants/courses'
import { calculateCourseHandicap, getStrokeHoles, resolvePlayerTeeRatings, resolvePlayerHandicapIndex } from '../utils/handicaps'
import { getGameType } from '../constants/gameTypes'
import { buildGameHandicapBreakdown } from '../utils/buildGameHandicapBreakdown'
import { computeRoundResults, computeMoneyResults, extractTeamPoints } from '../utils/scoringEngine'
import { computeCtpMoney, computeLowNetMoney, computeMatchLowNetMoney, computeMatchLowGrossMoney, computeMatchStablefordMoney, computeSkinsMoney, computeMatchSkinsMoney } from '../utils/sideBetEngine'
import { computePotDistribution } from '../utils/payoutEngine'

/**
 * Shared money-by-player calculation used by MoneyBoard and SettleUp.
 * Returns { [playerId]: { playerId, player, total, mainGame, sideBets, ctpMoney, lowNetMoney, lowGrossMoney, stablefordMoney, skinsMoney, buyIn } }
 */
export function useMoneyByPlayer({ match, matchGames, sideBets, viewedSessionId, viewedGameId }) {
  const matchPlayers = match?.match_players || []

  return useMemo(() => {
    const totals = {}
    matchPlayers.forEach((mp) => {
      totals[mp.player_id] = { playerId: mp.player_id, player: mp.player, total: 0, mainGame: 0, sideBets: 0, ctpMoney: 0, lowNetMoney: 0, lowGrossMoney: 0, stablefordMoney: 0, skinsMoney: 0, buyIn: 0 }
    })

    if (!matchGames) return totals

    const scorableGames = matchGames.filter(g => g.status === 'completed' || g.status === 'in_progress')
    const completedGames = matchGames.filter(g => g.status === 'completed')

    // --- Main game money ---
    for (const game of scorableGames) {
      if (!game.game_type || game.game_type === 'stroke') continue
      if (!game.wager || Number(game.wager) <= 0) continue

      const teams = game.teams || []
      const scores = game.scores || []
      if (teams.length < 2 || scores.length === 0) continue

      if (game.status === 'completed' && game.final_results?.moneyResults) {
        for (const mr of game.final_results.moneyResults) {
          if (totals[mr.playerId]) {
            totals[mr.playerId].total += mr.amount
            totals[mr.playerId].mainGame += mr.amount
          }
        }
        continue
      }

      const teamStructure = teams
        .sort((a, b) => a.team_order - b.team_order)
        .map((t) => ({
          name: t.team_name,
          playerIds: (t.team_players || []).map((tp) => tp.player_id),
        }))

      const course = getGameCourse(game)
      const tee = getGameTee(game)
      const handicapBreakdown = buildGameHandicapBreakdown({
        gameType: game.game_type, teamStructure, matchPlayers,
        gamePlayers: game.game_players || [], course, tee,
        mixedTees: getMixedTees(game),
      })

      const presses = (game.bets || []).filter((b) => b.bet_type === 'press')
      const pressData = presses.map((p) => ({
        segment: p.metadata?.segment,
        startHole: p.hole_start,
        wager: Number(p.amount),
        pressing_team: p.metadata?.pressing_team,
      }))

      const numHoles = game.num_holes || 18
      const isLive = game.status === 'in_progress'
      const cfg = { ...(game.game_config || {}) }
      if (course) cfg.holePars = course.par
      const results = computeRoundResults(game.game_type, teamStructure, scores, handicapBreakdown, numHoles, cfg)
      const moneyResults = computeMoneyResults(results, teamStructure, Number(game.wager), game.game_type, pressData, { liveMode: isLive, nassauWeights: game.game_config?.nassauWeights })

      for (const mr of moneyResults) {
        if (totals[mr.playerId]) {
          totals[mr.playerId].total += mr.amount
          totals[mr.playerId].mainGame += mr.amount
        }
      }
    }

    // --- Side bet money ---
    if (sideBets?.length > 0) {
      const matchLowNet = sideBets.find((b) => b.bet_type === 'low_net' && !b.game_id && b.scope === 'match')
      const matchLowGross = sideBets.find((b) => b.bet_type === 'low_gross' && !b.game_id && b.scope === 'match')
      const matchStableford = sideBets.find((b) => b.bet_type === 'stableford' && !b.game_id && b.scope === 'match')

      // CTP & Skins: process both completed AND in-progress games
      for (const game of scorableGames) {
        const course = getGameCourse(game)
        const tee = getGameTee(game)
        const numHoles = game.num_holes || 18
        const gamePlayers = game.game_players || []
        const gamePlayerIds = gamePlayers.map((gp) => gp.player_id)

        // CTP
        const ctpBet = sideBets.find((b) => b.bet_type === 'ctp' && b.game_id === game.id)

        if (ctpBet && game.status === 'completed' && game.final_results?.sideBets?.ctp) {
          const ctpMoney = game.final_results.sideBets.ctp
          for (const pid of gamePlayerIds) {
            if (totals[pid] && ctpMoney.byPlayer[pid] !== undefined) {
              totals[pid].total += ctpMoney.byPlayer[pid]
              totals[pid].sideBets += ctpMoney.byPlayer[pid]
              totals[pid].ctpMoney += ctpMoney.byPlayer[pid]
            }
          }
        } else if (ctpBet && course) {
          const par3Holes = course.par
            .map((p, i) => p === 3 ? i + 1 : null)
            .filter((h) => h != null && h <= numHoles)

          const ctpResults = game.ctp_results || []
          if (ctpResults.length > 0 && par3Holes.length > 0) {
            const ctpMoney = computeCtpMoney(ctpResults, ctpBet, gamePlayerIds, par3Holes)
            for (const pid of gamePlayerIds) {
              if (totals[pid] && ctpMoney.byPlayer[pid] !== undefined) {
                totals[pid].total += ctpMoney.byPlayer[pid]
                totals[pid].sideBets += ctpMoney.byPlayer[pid]
                totals[pid].ctpMoney += ctpMoney.byPlayer[pid]
              }
            }
          }
        }

        // Skins
        const roundSkinsBet = sideBets.find((b) => (b.bet_type === 'skins' || b.bet_type === 'skins_carry') && b.game_id === game.id)

        if (roundSkinsBet && game.status === 'completed' && game.final_results?.sideBets?.skins) {
          const skinsResult = game.final_results.sideBets.skins
          for (const pid of gamePlayerIds) {
            if (totals[pid] && skinsResult.byPlayer[pid] !== undefined) {
              totals[pid].total += skinsResult.byPlayer[pid]
              totals[pid].sideBets += skinsResult.byPlayer[pid]
              totals[pid].skinsMoney += skinsResult.byPlayer[pid]
            }
          }
        } else if (roundSkinsBet && course && tee) {
          const scores = game.scores || []
          let skinsHcp
          if (roundSkinsBet.config?.full_handicap) {
            const totalPar = course.par.reduce((s, p) => s + p, 0)
            const mixedTees = getMixedTees(game)
            skinsHcp = matchPlayers
              .filter(mp => gamePlayers.some(gp => gp.player_id === mp.player_id))
              .map(mp => {
                const playerTee = mixedTees?.[mp.player_id] || tee
                const { slope, rating } = resolvePlayerTeeRatings(playerTee, mp.gender || 'M')
                const courseHandicap = calculateCourseHandicap(Number(mp.handicap_index), slope, rating, totalPar)
                const rankings = resolvePlayerHandicapIndex(playerTee, mp.gender || 'M', course.handicapIndex)
                return { playerId: mp.player_id, courseHandicap, strokeHoles: getStrokeHoles(courseHandicap, rankings) }
              })
          } else {
            const skinsTeamStructure = (game.teams || [])
              .sort((a, b) => a.team_order - b.team_order)
              .map((t) => ({ playerIds: (t.team_players || []).map((tp) => tp.player_id) }))
            skinsHcp = buildGameHandicapBreakdown({
              gameType: game.game_type, teamStructure: skinsTeamStructure, matchPlayers,
              gamePlayers, course, tee, mixedTees: getMixedTees(game),
            })
          }
          if (!skinsHcp) continue
          const skinsResult = computeSkinsMoney(scores, roundSkinsBet, skinsHcp, numHoles, gamePlayerIds, roundSkinsBet.bet_type === 'skins_carry', roundSkinsBet.config?.gross_tiebreaker)
          for (const pid of gamePlayerIds) {
            if (totals[pid] && skinsResult.byPlayer[pid] !== undefined) {
              totals[pid].total += skinsResult.byPlayer[pid]
              totals[pid].sideBets += skinsResult.byPlayer[pid]
              totals[pid].skinsMoney += skinsResult.byPlayer[pid]
            }
          }
        }
      }

      // Low Net — completed games only
      for (const game of completedGames) {
        const gamePlayers = game.game_players || []
        const gamePlayerIds = gamePlayers.map((gp) => gp.player_id)

        const roundLowNet = sideBets.find((b) => b.bet_type === 'low_net' && b.game_id === game.id && b.scope === 'round')

        if (roundLowNet && game.final_results?.sideBets?.lowNet) {
          const lowNetResult = game.final_results.sideBets.lowNet
          for (const pid of gamePlayerIds) {
            if (totals[pid] && lowNetResult.byPlayer[pid] !== undefined) {
              totals[pid].total += lowNetResult.byPlayer[pid]
              totals[pid].sideBets += lowNetResult.byPlayer[pid]
              totals[pid].lowNetMoney += lowNetResult.byPlayer[pid]
            }
          }
        } else if (roundLowNet) {
          const course = getGameCourse(game)
          const tee = getGameTee(game)
          const numHoles = game.num_holes || 18
          const scores = game.scores || []

          if (course && tee) {
            const totalPar = course.par.reduce((s, p) => s + p, 0)
            const mixedTees = getMixedTees(game)
            const handicapBreakdown = matchPlayers
              .filter((mp) => gamePlayers.some((gp) => gp.player_id === mp.player_id))
              .map((mp) => {
                const playerTee = mixedTees?.[mp.player_id] || tee
                const { slope, rating } = resolvePlayerTeeRatings(playerTee, mp.gender || 'M')
                const courseHandicap = calculateCourseHandicap(Number(mp.handicap_index), slope, rating, totalPar)
                const rankings = resolvePlayerHandicapIndex(playerTee, mp.gender || 'M', course.handicapIndex)
                return { playerId: mp.player_id, courseHandicap, strokeHoles: getStrokeHoles(courseHandicap, rankings) }
              })

            const lowNetResult = computeLowNetMoney(scores, roundLowNet, handicapBreakdown, numHoles, gamePlayerIds)
            for (const pid of gamePlayerIds) {
              if (totals[pid] && lowNetResult.byPlayer[pid] !== undefined) {
                totals[pid].total += lowNetResult.byPlayer[pid]
                totals[pid].sideBets += lowNetResult.byPlayer[pid]
                totals[pid].lowNetMoney += lowNetResult.byPlayer[pid]
              }
            }
          }
        }
      }

      // Match-long Lowest Net
      if (matchLowNet && match?.status === 'completed' && match?.final_results?.matchLowNet) {
        const matchLowNetResult = match.final_results.matchLowNet
        const allPlayerIds = Object.keys(matchLowNetResult.byPlayer)
        for (const pid of allPlayerIds) {
          if (totals[pid] && matchLowNetResult.byPlayer[pid] !== undefined) {
            totals[pid].total += matchLowNetResult.byPlayer[pid]
            totals[pid].sideBets += matchLowNetResult.byPlayer[pid]
            totals[pid].lowNetMoney += matchLowNetResult.byPlayer[pid]
          }
        }
      } else if (matchLowNet && completedGames.length > 0) {
        const allPlayerIds = matchPlayers
          .filter((mp) => new Date(mp.created_at) <= new Date(matchLowNet.created_at))
          .map((mp) => mp.player_id)
        const roundsData = completedGames.map((game) => {
          const course = getGameCourse(game)
          const tee = getGameTee(game)
          const gamePlayers = game.game_players || []
          const playerIds = gamePlayers.map((gp) => gp.player_id)
          let handicapBreakdown = null
          if (course && tee) {
            const totalPar = course.par.reduce((s, p) => s + p, 0)
            const mixedTees = getMixedTees(game)
            handicapBreakdown = matchPlayers
              .filter((mp) => gamePlayers.some((gp) => gp.player_id === mp.player_id))
              .map((mp) => {
                const playerTee = mixedTees?.[mp.player_id] || tee
                const { slope, rating } = resolvePlayerTeeRatings(playerTee, mp.gender || 'M')
                const courseHandicap = calculateCourseHandicap(Number(mp.handicap_index), slope, rating, totalPar)
                const rankings = resolvePlayerHandicapIndex(playerTee, mp.gender || 'M', course.handicapIndex)
                return { playerId: mp.player_id, courseHandicap, strokeHoles: getStrokeHoles(courseHandicap, rankings) }
              })
            if (handicapBreakdown.length === 0) handicapBreakdown = null
          }
          return { scores: game.scores || [], handicapBreakdown, numHoles: game.num_holes || 18, playerIds }
        }).filter((r) => r.handicapBreakdown)

        if (roundsData.length > 0) {
          const matchLowNetResult = computeMatchLowNetMoney(roundsData, matchLowNet, allPlayerIds)
          for (const pid of allPlayerIds) {
            if (totals[pid] && matchLowNetResult.byPlayer[pid] !== undefined) {
              totals[pid].total += matchLowNetResult.byPlayer[pid]
              totals[pid].sideBets += matchLowNetResult.byPlayer[pid]
              totals[pid].lowNetMoney += matchLowNetResult.byPlayer[pid]
            }
          }
        }
      }

      // Match-long Lowest Gross
      if (matchLowGross && match?.status === 'completed' && match?.final_results?.matchLowGross) {
        const result = match.final_results.matchLowGross
        const allPlayerIds = Object.keys(result.byPlayer)
        for (const pid of allPlayerIds) {
          if (totals[pid] && result.byPlayer[pid] !== undefined) {
            totals[pid].total += result.byPlayer[pid]
            totals[pid].sideBets += result.byPlayer[pid]
            totals[pid].lowGrossMoney += result.byPlayer[pid]
          }
        }
      } else if (matchLowGross && completedGames.length > 0) {
        const allPlayerIds = matchPlayers
          .filter((mp) => new Date(mp.created_at) <= new Date(matchLowGross.created_at))
          .map((mp) => mp.player_id)
        const roundsData = completedGames.map((game) => {
          const gamePlayers = game.game_players || []
          const playerIds = gamePlayers.map((gp) => gp.player_id)
          return { scores: game.scores || [], numHoles: game.num_holes || 18, playerIds }
        })

        if (roundsData.length > 0) {
          const result = computeMatchLowGrossMoney(roundsData, matchLowGross, allPlayerIds)
          for (const pid of allPlayerIds) {
            if (totals[pid] && result.byPlayer[pid] !== undefined) {
              totals[pid].total += result.byPlayer[pid]
              totals[pid].sideBets += result.byPlayer[pid]
              totals[pid].lowGrossMoney += result.byPlayer[pid]
            }
          }
        }
      }

      // Match-long Stableford
      if (matchStableford && match?.status === 'completed' && match?.final_results?.matchStableford) {
        const result = match.final_results.matchStableford
        const allPlayerIds = Object.keys(result.byPlayer)
        for (const pid of allPlayerIds) {
          if (totals[pid] && result.byPlayer[pid] !== undefined) {
            totals[pid].total += result.byPlayer[pid]
            totals[pid].sideBets += result.byPlayer[pid]
            totals[pid].stablefordMoney += result.byPlayer[pid]
          }
        }
      } else if (matchStableford && completedGames.length > 0) {
        const allPlayerIds = matchPlayers
          .filter((mp) => new Date(mp.created_at) <= new Date(matchStableford.created_at))
          .map((mp) => mp.player_id)
        const roundsData = completedGames.map((game) => {
          const course = getGameCourse(game)
          const tee = getGameTee(game)
          const gamePlayers = game.game_players || []
          const playerIds = gamePlayers.map((gp) => gp.player_id)
          let handicapBreakdown = null
          if (course && tee) {
            const totalPar = course.par.reduce((s, p) => s + p, 0)
            const mixedTees = getMixedTees(game)
            handicapBreakdown = matchPlayers
              .filter((mp) => gamePlayers.some((gp) => gp.player_id === mp.player_id))
              .map((mp) => {
                const playerTee = mixedTees?.[mp.player_id] || tee
                const { slope, rating } = resolvePlayerTeeRatings(playerTee, mp.gender || 'M')
                const courseHandicap = calculateCourseHandicap(Number(mp.handicap_index), slope, rating, totalPar)
                const rankings = resolvePlayerHandicapIndex(playerTee, mp.gender || 'M', course.handicapIndex)
                return { playerId: mp.player_id, courseHandicap, strokeHoles: getStrokeHoles(courseHandicap, rankings) }
              })
            if (handicapBreakdown.length === 0) handicapBreakdown = null
          }
          return { scores: game.scores || [], handicapBreakdown, numHoles: game.num_holes || 18, playerIds, holePars: course?.par || null }
        }).filter((r) => r.handicapBreakdown)

        if (roundsData.length > 0) {
          const result = computeMatchStablefordMoney(roundsData, matchStableford, allPlayerIds)
          for (const pid of allPlayerIds) {
            if (totals[pid] && result.byPlayer[pid] !== undefined) {
              totals[pid].total += result.byPlayer[pid]
              totals[pid].sideBets += result.byPlayer[pid]
              totals[pid].stablefordMoney += result.byPlayer[pid]
            }
          }
        }
      }

      // Match-long Skins
      const matchSkins = sideBets.find((b) => (b.bet_type === 'skins' || b.bet_type === 'skins_carry') && !b.game_id && b.scope === 'match')
      if (matchSkins && match?.status === 'completed' && match?.final_results?.matchSkins) {
        const result = match.final_results.matchSkins
        const allPlayerIds = Object.keys(result.byPlayer)
        for (const pid of allPlayerIds) {
          if (totals[pid] && result.byPlayer[pid] !== undefined) {
            totals[pid].total += result.byPlayer[pid]
            totals[pid].sideBets += result.byPlayer[pid]
            totals[pid].skinsMoney += result.byPlayer[pid]
          }
        }
      } else if (matchSkins && completedGames.length > 0) {
        const allPlayerIds = matchPlayers
          .filter((mp) => new Date(mp.created_at) <= new Date(matchSkins.created_at))
          .map((mp) => mp.player_id)
        const roundsData = completedGames.map((game) => {
          const course = getGameCourse(game)
          const tee = getGameTee(game)
          const gamePlayers = game.game_players || []
          const playerIds = gamePlayers.map((gp) => gp.player_id)
          let handicapBreakdown = null
          if (course && tee) {
            const totalPar = course.par.reduce((s, p) => s + p, 0)
            const mixedTees = getMixedTees(game)
            handicapBreakdown = matchPlayers
              .filter((mp) => gamePlayers.some((gp) => gp.player_id === mp.player_id))
              .map((mp) => {
                const playerTee = mixedTees?.[mp.player_id] || tee
                const { slope, rating } = resolvePlayerTeeRatings(playerTee, mp.gender || 'M')
                const courseHandicap = calculateCourseHandicap(Number(mp.handicap_index), slope, rating, totalPar)
                const rankings = resolvePlayerHandicapIndex(playerTee, mp.gender || 'M', course.handicapIndex)
                return { playerId: mp.player_id, courseHandicap, strokeHoles: getStrokeHoles(courseHandicap, rankings) }
              })
            if (handicapBreakdown.length === 0) handicapBreakdown = null
          }
          return { scores: game.scores || [], handicapBreakdown, numHoles: game.num_holes || 18, playerIds }
        }).filter((r) => r.handicapBreakdown)

        if (roundsData.length > 0) {
          const result = computeMatchSkinsMoney(roundsData, matchSkins, allPlayerIds, matchSkins.bet_type === 'skins_carry', matchSkins.config?.gross_tiebreaker)
          for (const pid of allPlayerIds) {
            if (totals[pid] && result.byPlayer[pid] !== undefined) {
              totals[pid].total += result.byPlayer[pid]
              totals[pid].sideBets += result.byPlayer[pid]
              totals[pid].skinsMoney += result.byPlayer[pid]
            }
          }
        }
      }
    }

    // --- Buy-in computation (all started rounds) ---
    const startedGames = matchGames.filter(g => g.status === 'completed' || g.status === 'in_progress')

    for (const game of startedGames) {
      const gamePlayers = game.game_players || []
      const gamePlayerIds = gamePlayers.map(gp => gp.player_id)

      // Round wager buy-in (non-stroke games with a wager)
      if (game.game_type && game.game_type !== 'stroke' && game.wager && Number(game.wager) > 0) {
        const wager = Number(game.wager)
        const gt = getGameType(game.game_type)
        const isStandard = game.game_config?.matchFormat === 'standard'
        const nassauWeightsSum = (game.game_config?.nassauWeights || [1, 1, 1]).reduce((a, b) => a + b, 0)
        const numBets = isStandard ? 1 : ['nassau', 'six_pack', 'stableford'].includes(gt?.scoring) ? nassauWeightsSum : gt?.scoring === 'per_nine' ? 2 : 1
        for (const pid of gamePlayerIds) {
          if (totals[pid]) totals[pid].buyIn += wager * numBets
        }
      }

      // Round-level side bet buy-ins
      if (sideBets?.length > 0) {
        // CTP: amount per par 3 hole
        const ctpBet = sideBets.find(b => b.bet_type === 'ctp' && b.game_id === game.id)
        if (ctpBet) {
          const course = getGameCourse(game)
          const numHoles = game.num_holes || 18
          if (course) {
            const par3Count = course.par.slice(0, numHoles).filter(p => p === 3).length
            const ctpBuyIn = Number(ctpBet.amount) * par3Count
            for (const pid of gamePlayerIds) {
              if (totals[pid]) totals[pid].buyIn += ctpBuyIn
            }
          }
        }

        // Round low net
        const roundLowNet = sideBets.find(b => b.bet_type === 'low_net' && b.game_id === game.id && b.scope === 'round')
        if (roundLowNet) {
          const amount = Number(roundLowNet.amount)
          for (const pid of gamePlayerIds) {
            if (totals[pid]) totals[pid].buyIn += amount
          }
        }

        // Round skins
        const roundSkinsBet = sideBets.find(b => (b.bet_type === 'skins' || b.bet_type === 'skins_carry') && b.game_id === game.id)
        if (roundSkinsBet) {
          const amount = Number(roundSkinsBet.amount)
          for (const pid of gamePlayerIds) {
            if (totals[pid]) totals[pid].buyIn += amount
          }
        }
      }
    }

    // Match-level side bet buy-ins
    if (sideBets?.length > 0) {
      for (const betType of ['low_net', 'low_gross', 'stableford', 'skins', 'skins_carry']) {
        const bet = sideBets.find(b => b.bet_type === betType && !b.game_id && b.scope === 'match')
        if (bet) {
          const amount = Number(bet.amount)
          for (const mp of matchPlayers) {
            if (totals[mp.player_id]) totals[mp.player_id].buyIn += amount
          }
        }
      }
    }

    // Main match buy-in (team_match / invitational)
    if (match?.buy_in && Number(match.buy_in) > 0 && !viewedSessionId && !viewedGameId) {
      const buyInAmount = Number(match.buy_in)
      for (const mp of matchPlayers) {
        if (totals[mp.player_id]) totals[mp.player_id].buyIn += buyInAmount
      }
    }

    // Pot distribution for team_match / invitational (buy-in pot distributed by team points)
    const matchTeams = match?.match_teams || []
    const isTeamMatch = match?.match_type === 'team_match' || match?.match_type === 'invitational'
    if (isTeamMatch && match?.buy_in > 0 && match?.payout_config?.splits?.length && matchTeams.length >= 2 && !viewedSessionId && !viewedGameId) {
      // Compute team points from session games
      const sessionGames = (matchGames || []).filter(g =>
        g.session_group_id && (g.status === 'completed' || g.status === 'in_progress')
      )
      const latestByGroup = new Map()
      for (const g of sessionGames) {
        const existing = latestByGroup.get(g.session_group_id)
        if (!existing || new Date(g.created_at) > new Date(existing.created_at)) {
          latestByGroup.set(g.session_group_id, g)
        }
      }

      const teamPoints = new Map()
      matchTeams.forEach(mt => teamPoints.set(mt.id, 0))

      for (const game of latestByGroup.values()) {
        const gameTeams = game.teams || []
        if (gameTeams.length < 2 || !game.game_type || game.game_type === 'stroke') continue

        const teamStructure = gameTeams
          .sort((a, b) => a.team_order - b.team_order)
          .map(t => ({ name: t.team_name, playerIds: (t.team_players || []).map(tp => tp.player_id) }))

        let results
        if (game.status === 'completed' && game.final_results?.roundResults) {
          results = game.final_results.roundResults
        } else {
          const course = getGameCourse(game)
          const tee = getGameTee(game)
          const hcp = buildGameHandicapBreakdown({
            gameType: game.game_type, teamStructure, matchPlayers,
            gamePlayers: game.game_players, course, tee, mixedTees: getMixedTees(game),
          })
          const cfg = { ...(game.game_config || {}) }
          if (course) cfg.holePars = course.par
          results = computeRoundResults(game.game_type, teamStructure, game.scores || [], hcp, game.num_holes || 18, cfg)
        }
        if (!results) continue

        const gamePoints = extractTeamPoints(results, teamStructure.length)
        if (!gamePoints) continue

        const sortedGameTeams = gameTeams.sort((a, b) => a.team_order - b.team_order)
        for (let i = 0; i < sortedGameTeams.length; i++) {
          const mt = matchTeams.find(m => m.team_name === sortedGameTeams[i].team_name)
          if (mt && teamPoints.has(mt.id)) {
            teamPoints.set(mt.id, teamPoints.get(mt.id) + (gamePoints[i] || 0))
          }
        }
      }

      // Build standings sorted by points desc
      const teamStandings = matchTeams
        .map(mt => ({ matchTeam: mt, points: teamPoints.get(mt.id) || 0 }))
        .sort((a, b) => b.points - a.points)

      // Only distribute if at least one team has points
      if (teamStandings.some(ts => ts.points > 0)) {
        const totalPot = matchPlayers.length * Number(match.buy_in)
        const distribution = computePotDistribution(teamStandings, match.payout_config, totalPot)
        const numTeams = matchTeams.length
        const playersPerTeam = numTeams > 0 ? matchPlayers.length / numTeams : 1

        // Each player's share = team payout / players on that team
        // Net = payout - buy_in (buy_in already tracked separately)
        for (const mt of matchTeams) {
          const payout = distribution.get(mt.id)
          if (!payout) continue
          const teamPlayerIds = (mt.match_team_players || []).map(mtp => mtp.player_id)
          const perPlayer = teamPlayerIds.length > 0 ? payout.amount / teamPlayerIds.length : 0
          const netPerPlayer = perPlayer - Number(match.buy_in)
          for (const pid of teamPlayerIds) {
            if (totals[pid]) {
              totals[pid].total += netPerPlayer
              totals[pid].mainGame += netPerPlayer
            }
          }
        }
      }
    }

    return totals
  }, [matchPlayers, matchGames, sideBets, match, viewedSessionId, viewedGameId])
}
